/**
 * useIntroVideo — hook for a student's Mux-backed intro video.
 *
 * Owns the full lifecycle:
 *   1. POST /api/videos/upload-url  → { upload_id, upload_url, id }
 *   2. PUT the file bytes to upload_url (direct-to-Mux, with progress)
 *   3. Poll GET /api/videos/{id} until status is "ready" or "errored"
 *   4. Return the ready record so the caller can render Mux Player
 */
import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "mra_jwt";

const client = axios.create({ baseURL: API, withCredentials: true });
client.interceptors.request.use((config) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${t}`;
  }
  return config;
});

const POLL_MS = 3000;
const POLL_MAX_MS = 5 * 60 * 1000; // 5-minute ceiling

export const useIntroVideo = ({ campaignId = null } = {}) => {
  const [videos, setVideos] = useState([]);
  const [current, setCurrent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const pollStartedRef = useRef(0);

  const refresh = useCallback(async () => {
    try {
      const res = await client.get("/videos");
      setVideos(res.data);
      const first = res.data[0] || null;
      setCurrent(first);
      return first;
    } catch {
      setVideos([]);
      setCurrent(null);
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [refresh]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) clearTimeout(pollRef.current);
    pollRef.current = null;
  }, []);

  const startPolling = useCallback((id) => {
    stopPolling();
    pollStartedRef.current = Date.now();
    const tick = async () => {
      try {
        const res = await client.get(`/videos/${id}`);
        setCurrent(res.data);
        setVideos((list) => list.map((v) => (v.id === id ? res.data : v)));
        if (res.data.status === "ready" || res.data.status === "errored" ||
            res.data.status === "cancelled") {
          return; // done
        }
      } catch {
        /* transient — keep polling */
      }
      if (Date.now() - pollStartedRef.current < POLL_MAX_MS) {
        pollRef.current = setTimeout(tick, POLL_MS);
      }
    };
    pollRef.current = setTimeout(tick, POLL_MS);
  }, [stopPolling]);

  const upload = useCallback(async (file) => {
    setError("");
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const { data } = await client.post("/videos/upload-url", {
        filename: file.name,
        content_type: file.type,
        size: file.size,
        campaign_id: campaignId,
      });
      // Direct-to-Mux PUT — no auth header, no withCredentials.
      await axios.put(data.upload_url, file, {
        headers: { "Content-Type": file.type || "application/octet-stream" },
        withCredentials: false,
        onUploadProgress: (evt) => {
          if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      // Immediately reflect processing state (webhook may still be en route)
      setCurrent({
        id: data.id,
        user_id: "",
        upload_id: data.upload_id,
        status: "processing",
        filename: file.name,
        content_type: file.type,
        size: file.size,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
      startPolling(data.id);
      // Also refresh the list from the backend
      await refresh();
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  }, [refresh, startPolling, campaignId]);

  const remove = useCallback(async (id) => {
    stopPolling();
    try {
      await client.delete(`/videos/${id}`);
    } catch { /* ignore */ }
    setCurrent(null);
    await refresh();
  }, [refresh, stopPolling]);

  const forceSync = useCallback(async (id) => {
    try {
      const res = await client.post(`/videos/${id}/sync`);
      setCurrent(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, []);

  return {
    videos,
    current,
    uploading,
    progress,
    error,
    upload,
    remove,
    forceSync,
    refresh,
  };
};
